import _ from 'lodash';
import classnames from 'classnames';
import moment from 'moment';
import React from 'react';
import { Button } from 'antd';
import memoizeOne from 'memoize-one';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { gql } from '@apollo/client';
import { Query } from '@apollo/react-components';
import SpinnerError from '$components/library/SpinnerError';
import SCREEN from '../screen';
import styles from './styles.scss';
import BURN_UP_CUTOFF from '../burnUpCutoff';

const USER_EXAM_PRESENTATIONS_QUERY = gql`
  query UserExam($input: UserExamInput!) {
    userExam(input: $input) {
      userExam {
        uuid
        userExamItems {
          choice
          code
          isFlagged
          itemEvaluated
          itemType
          itemUuid
          position
          seed
          score
          status
          uuid
        }
        presentations {
          uuid
          itemUuid
          seenAt
          totalMs
        }
      }
    }
  }
`;

const getBurnUpDataSet = memoizeOne((presentations, userExam) => {
  const { userExamItems } = userExam;
  const augmentedUserExamItems = _.cloneDeep(userExamItems);
  const userExamItemsByItemUuid = _.keyBy(augmentedUserExamItems, 'itemUuid');
  const positionByItemUuid = _.mapValues(_.keyBy(userExamItems, 'itemUuid'), 'position');
  const sortedPresentations = _.sortBy(presentations, 'seenAt');
  let totalExamMs = 0;
  const presentationsWithPosition = _.map(sortedPresentations, (presentation) => {
    const { itemUuid, totalMs } = presentation;
    const position = positionByItemUuid[itemUuid];
    const seenAtSeconds = (totalExamMs + totalMs) / 1000;
    totalExamMs += totalMs;

    const userExamItem = userExamItemsByItemUuid[itemUuid];
    if (userExamItem) {
      userExamItem.totalMs = (userExamItem.totalMs || 0) + totalMs;
    }

    return {
      ...presentation,
      position,
      seenAtSeconds,
    };
  });

  _.forEach(sortedPresentations, (presentation) => {
    const { itemUuid, totalMs } = presentation;
    const userExamItem = userExamItemsByItemUuid[itemUuid];
    userExamItem.totalMs = (userExamItem.totalMs || 0) + totalMs;
  });

  const firstPresentation = _.first(presentationsWithPosition);

  presentationsWithPosition.unshift({
    ...firstPresentation,
    seenAtSeconds: 0,
  });

  return {
    presentationsWithPosition,
    augmentedUserExamItems,
  };
});

const SlowestItems = (props) => {
  const { augmentedUserExamItems, handleQueueNavigation } = props;
  const slowestSortedItems = _.sortBy(
    augmentedUserExamItems,
    (item) => item.totalMs || 0
  ).reverse();
  const slowestItems = _.slice(slowestSortedItems, 0, 5);

  const navigateToItem = (position) => {
    const queueIndex = position - 1;
    handleQueueNavigation({ queueIndex });
  };

  return (
    <div>
      <div className={styles.header}>Slowest questions</div>
      <div>
        {slowestItems.map((item) => (
          <div key={item.uuid} className={styles.slowItem}>
            <div className={styles.icon}>
              {item.status === 'correct' ? (
                <i className={classnames('fad', 'fa-check', styles.correct)} />
              ) : (
                '❌'
              )}
            </div>
            Question&nbsp;
            <div className={styles.itemLink} onClick={() => navigateToItem(item.position)}>#{item.position}</div>&nbsp;-&nbsp;
            {moment.duration(item.totalMs).humanize()}
          </div>
        ))}
      </div>
    </div>
  );
};

const BurnUpChart = (props) => {
  const { handleQueueNavigation, presentations, userExam } = props;
  const { augmentedUserExamItems, presentationsWithPosition } = getBurnUpDataSet(
    presentations,
    userExam
  );

  return (
    <>
      <div className={styles.header}>Burn up chart (beta)</div>
      <div>How did you spend your time?</div>
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={presentationsWithPosition}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" dataKey="seenAtSeconds" />
          <YAxis allowDecimals={false} />
          <Line type="stepBefore" dataKey="position" stroke="#8884d8" dot={false} />
          <Tooltip />
        </LineChart>
      </ResponsiveContainer>
      <SlowestItems
        augmentedUserExamItems={augmentedUserExamItems}
        handleQueueNavigation={handleQueueNavigation}
      />
    </>
  );
};

const BurnUp = (props) => {
  const { handleQueueNavigation, setScreen, userExam } = props;
  const { uuid: userExamUuid } = userExam;
  return (
    <div className={styles.layout}>
      <Query
        query={USER_EXAM_PRESENTATIONS_QUERY}
        variables={{ input: { uuid: userExamUuid } }}
      >
        {({ loading, error, data }) => {
          if (loading || error) {
            return <SpinnerError error={error} />;
          }

          const {
            userExam: {
              userExam: gradedUserExam,
            },
          } = data;
          const { presentations } = gradedUserExam;

          if (presentations.length === 0) {
            return (
              <div>
                <div>Unable to display burn up chart for this exam :-(</div>
                <br />
                <div>
                  Most exams taken after {BURN_UP_CUTOFF} will be compatible with this
                  feature.
                </div>
              </div>
            );
          }

          return (
            <BurnUpChart
              presentations={presentations}
              userExam={gradedUserExam}
              handleQueueNavigation={handleQueueNavigation}
            />
          );
        }}
      </Query>
      <br />
      <Button onClick={() => setScreen(SCREEN.summary)}>Back to summary</Button>
    </div>
  );
};

export default BurnUp;
